import { useState } from "react";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  CHOOSABLE_ORGANISATION_TYPES,
  CreateOrganisationRequestSchema,
  type ConfigurationScopeSummary,
  type CreateOrganisationRequest,
} from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../../components/CancelOrProceedActions/CancelOrProceedActions.js";
import { asChains, chainChoices } from "./configurationChains.js";
import type { ReadOutcome } from "./readOutcome.js";

/**
 * Whether the organisation was added. One `failed` for every way it was not:
 * nothing the person in front of the dialog could do differently tells a
 * refusal from an unreachable server.
 */
export type AddOrganisationOutcome = "added" | "failed";

/**
 * Asks for a new organisation's name, kind and configuration, and adds it.
 *
 * All three are required, checked against the schema the server parses with,
 * so the button only offers a request the server's validation will accept.
 */
export const AddOrganisationDialog = ({
  open,
  onClose,
  onAdd,
  configurations,
}: {
  open: boolean;
  onClose: () => Promisable<void>;
  onAdd: (
    organisation: CreateOrganisationRequest,
  ) => Promisable<AddOrganisationOutcome>;
  /** The configurations a new organisation can be set to. */
  configurations: ReadOutcome<readonly ConfigurationScopeSummary[]>;
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [configurationScopeId, setConfigurationScopeId] = useState("");
  const [status, setStatus] = useState<"adding" | "failed">();

  const parsed = CreateOrganisationRequestSchema.safeParse({
    name,
    type,
    configurationScopeId,
  });
  const adding = status === "adding";
  const { choices, hint } = match(configurations)
    .with({ kind: "reading" }, () => ({
      choices: [],
      hint: t("admin.organisations.add.configurationsLoading"),
    }))
    .with({ kind: "failed" }, () => ({
      choices: [],
      hint: t("admin.organisations.add.configurationsFailed"),
    }))
    .with({ kind: "read" }, ({ value }) => {
      const offered = chainChoices({ chains: asChains(value), t });

      return {
        choices: offered,
        hint:
          offered.length === 0
            ? t("admin.organisations.add.noConfigurations")
            : t("admin.organisations.add.configurationHint"),
      };
    })
    .exhaustive();

  const reset = () => {
    setName("");
    setType("");
    setConfigurationScopeId("");
    setStatus(undefined);
  };

  const close = () => {
    reset();
    void onClose();
  };

  const add = async () => {
    if (!parsed.success) {
      return;
    }

    setStatus("adding");
    let outcome: AddOrganisationOutcome;

    try {
      outcome = await onAdd(parsed.data);
    } catch {
      outcome = "failed";
    }

    if (outcome === "failed") {
      setStatus("failed");
      return;
    }

    reset();
  };

  return (
    <Dialog open={open} onClose={adding ? undefined : close} fullWidth>
      <DialogTitle>{t("admin.organisations.add.title")}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          required
          margin="normal"
          label={t("admin.organisations.add.name")}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextField
          select
          fullWidth
          required
          margin="normal"
          label={t("admin.organisations.add.type")}
          value={type}
          onChange={(event) => {
            setType(event.target.value);
          }}
        >
          {CHOOSABLE_ORGANISATION_TYPES.map((each) => (
            <MenuItem key={each} value={each}>
              {t(`organisation.type.${each}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          required
          margin="normal"
          label={t("admin.organisations.add.configuration")}
          helperText={hint}
          value={configurationScopeId}
          onChange={(event) => {
            setConfigurationScopeId(event.target.value);
          }}
        >
          {choices.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>
              {choice.label}
            </MenuItem>
          ))}
        </TextField>
        {status === "failed" ? (
          <Alert severity="error">{t("admin.organisations.add.failed")}</Alert>
        ) : null}
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: adding,
        }}
        proceed={{
          label: t("admin.organisations.add.submit"),
          onClick: add,
          disabled: !parsed.success || adding,
          kind: "primary",
        }}
      />
    </Dialog>
  );
};
